/**
 * toast-service: API imperativa window.ExtUI.toast.
 *
 * Crea un contenedor global al primer uso, spawns <ext-toast> elements,
 * y los limpia cuando emiten ext:dismiss. El contenedor vive en document.body,
 * independiente del shadow tree de cualquier host.
 */
import './ext-toast';
import type { ToastOptions, ExtUIRegistry } from '../../core/registry';

const CONTAINER_ID = 'ext-ui-toast-container';

interface ExtToastElement extends HTMLElement {
  kind?: string;
  message?: string;
  duration?: number;
}

function getContainer(): HTMLElement {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'false');
    document.body.appendChild(container);
  }
  return container;
}

function show(opts: ToastOptions): string {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const el = document.createElement('ext-toast') as ExtToastElement;
  el.id = id;
  el.setAttribute('kind', opts.kind || 'info');
  el.setAttribute('message', opts.message);
  if (opts.duration !== undefined) {
    el.setAttribute('duration', String(opts.duration));
  }
  el.addEventListener('ext:dismiss', () => {
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  });
  getContainer().appendChild(el);
  return id;
}

function dismiss(id: string): void {
  const el = document.getElementById(id);
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

export function registerToastService(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { ExtUI?: Partial<ExtUIRegistry> };
  if (!w.ExtUI) {
    w.ExtUI = {} as Partial<ExtUIRegistry>;
  }
  w.ExtUI.toast = { show, dismiss };
}
